import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { assert } from '@ember/debug';

export interface TextareaControlSignature {
  Element: HTMLTextAreaElement;
  Args: {
    isConfirmed?: boolean;
    hasError?: boolean;
    onChange?: (value: string, e: Event | InputEvent) => void;
    value?: string;
    ariaLabel?: string;
  };
}

const STYLES = {
  base: [
    'form-input',
    '[field-sizing:content]',
    'text-16',
    'sm:text-14',
    'px-0',
    'py-2',
    'sm:py-0',
    'w-full',
    'bg-transparent',
    'placeholder:text-grey',
    'border-b-grey',
    'border-l-0',
    'border-r-0',
    'border-t-0',
    'focus:border-b-black',
    'focus:ring-0',
    '[&:disabled]:border-b-grey-light',
    '[&:disabled]:text-grey-light',
    '[&:disabled]:placeholder:text-transparent'
  ],
  confirmed: [
    'text-teal',
    'border-b-teal',
    'focus:border-b-teal',
    'focus:shadow-confirmed-input'
  ],
  error: [
    'text-red',
    'border-b-red',
    'focus:border-b-red',
    'focus:shadow-error-input'
  ]
} as const;

export default class WrFormControlsTextareaComponent extends Component<TextareaControlSignature> {
  get styles() {
    let styles;

    if (this.args.isConfirmed) styles = [...STYLES.base, ...STYLES.confirmed];
    else if (this.args.hasError) styles = [...STYLES.base, ...STYLES.error];
    else styles = [...STYLES.base, 'focus:shadow-input'];

    return styles.join(' ');
  }

  handleInput = (e: Event | InputEvent): void => {
    assert(
      'Expected HTMLTextAreaElement',
      e.target instanceof HTMLTextAreaElement
    );
    this.args.onChange?.(e.target.value, e);
  };

  <template>
    <textarea
      aria-label={{@ariaLabel}}
      class={{this.styles}}
      {{on 'input' this.handleInput}}
      rows='1'
      ...attributes
    >{{@value}}</textarea>
  </template>
}
